import { Component, OnInit, computed, inject, input, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { FormDropdownService } from '../../services/form-dropdown.service';
import {
  DEFAULT_PHONE_PREFIX,
  MAX_PHONE_DIGITS,
  MIN_PHONE_DIGITS,
  PhonePrefixOption,
  isPhoneNumberDigitsOnly,
  isValidNationalPhoneNumber,
} from './phone-prefixes';

/**
 * Campo telefono/cellulare con tendina del prefisso internazionale.
 *
 * Prefisso e numero sono due valori distinti scambiati con il contenitore
 * (`[(prefix)]` e `[(number)]`) e salvati su due colonne separate a database.
 * Il numero contiene solo cifre (6-15); il prefisso e' nel formato "+39".
 */
@Component({
  selector: 'app-phone-input',
  standalone: true,
  imports: [CommonModule, FormsModule, InputTextModule, SelectModule],
  template: `
    <label [for]="inputId()" class="font-semibold block mb-2">
      {{ label() }}
      @if (required()) {
        <span class="text-red-500" aria-hidden="true">*</span>
      }
    </label>

    <div class="flex gap-2">
      <p-select
        [id]="inputId() + '-prefix'"
        [ngModel]="selectedPrefix()"
        (ngModelChange)="onPrefixChange($event)"
        [options]="prefixOptions()"
        optionLabel="label"
        optionValue="code"
        [filter]="true"
        filterBy="label"
        [showClear]="false"
        [disabled]="isDisabled()"
        styleClass="flex-shrink-0"
        [style]="{ width: '9rem' }"
        [attr.aria-label]="'Prefisso internazionale per ' + label()"
      />
      <input
        [id]="inputId()"
        type="tel"
        pInputText
        class="w-full"
        [ngModel]="number()"
        (ngModelChange)="onNumberChange($event)"
        (keydown)="onNumberKeydown($event)"
        (blur)="onBlur($event)"
        [readonly]="isDisabled()"
        [disabled]="isDisabled()"
        [placeholder]="placeholder()"
        inputmode="numeric"
        autocomplete="tel-national"
        [attr.maxlength]="maxDigits"
        [attr.aria-required]="required()"
        [attr.aria-invalid]="showError()"
        [attr.aria-describedby]="(showError() ? inputId() + '-error' : inputId() + '-hint') + (description() ? ' ' + inputId() + '-description' : '')"
      />
    </div>

    @if (description()) {
      <small [id]="inputId() + '-description'" class="text-color-secondary text-xs block mt-1">
        {{ description() }}
      </small>
    }

    @if (showError()) {
      <small [id]="inputId() + '-error'" class="text-red-500 block mt-1" role="alert">
        {{ errorMessage() }}
      </small>
    } @else {
      <small [id]="inputId() + '-hint'" class="text-color-secondary text-xs block mt-1">
        Solo cifre, da {{ minDigits }} a {{ maxDigits }}, senza spazi. Prefisso escluso dal conteggio.
      </small>
    }
  `,
  styles: [
    `
      /* In sola lettura allineiamo l'aspetto del numero a quello degli altri
         campi readonly (sfondo grigio): la regola .read-only-mode del padre
         non attraversa il DOM incapsulato di questo componente. */
      input[readonly],
      input:disabled {
        background-color: #f3f4f6;
        color: #1f2937;
        border-color: #d1d5db;
        cursor: not-allowed;
        opacity: 1;
        -webkit-text-fill-color: #1f2937;
      }
    `,
  ],
})
export class PhoneInputComponent implements OnInit {
  private formDropdownService = inject(FormDropdownService);

  /** Prefisso internazionale, es. "+39". */
  prefix = model<string>(DEFAULT_PHONE_PREFIX);
  /** Numero nazionale, solo cifre e senza prefisso. */
  number = model<string>('');

  inputId = input.required<string>();
  label = input<string>('');
  description = input<string>('');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  placeholder = input<string>('es. 3351234567');
  /** Forza la visualizzazione dell'errore anche senza interazione (es. al salvataggio). */
  forceShowError = input<boolean>(false);

  blurred = output<void>();

  /** Prefissi caricati dal backend (dropdown PHONE_PREFIX). */
  readonly prefixOptions = signal<PhonePrefixOption[]>([]);
  readonly minDigits = MIN_PHONE_DIGITS;
  readonly maxDigits = MAX_PHONE_DIGITS;

  private readonly touched = signal(false);

  ngOnInit(): void {
    this.formDropdownService.getPhonePrefixes().subscribe({
      next: (options) => this.prefixOptions.set(options),
      error: (err) => console.error('Errore caricamento prefissi telefonici:', err),
    });
  }

  readonly isDisabled = computed(() => this.disabled());
  readonly selectedPrefix = computed(() => this.prefix() || DEFAULT_PHONE_PREFIX);

  readonly showError = computed(() => {
    if (!this.touched() && !this.forceShowError()) {
      return false;
    }
    return this.errorMessage() !== '';
  });

  readonly errorMessage = computed(() => {
    const number = this.number();

    if (!number) {
      return this.required() ? 'Campo obbligatorio' : '';
    }
    if (!isPhoneNumberDigitsOnly(number)) {
      return 'Il campo deve contenere soltanto numeri.';
    }
    if (!isValidNationalPhoneNumber(number)) {
      return `Il numero deve contenere da ${this.minDigits} a ${this.maxDigits} cifre.`;
    }
    return '';
  });

  onPrefixChange(prefix: string): void {
    if (this.isDisabled()) {
      return;
    }
    this.prefix.set(prefix || DEFAULT_PHONE_PREFIX);
  }

  onNumberChange(rawNumber: string): void {
    if (this.isDisabled()) {
      return;
    }
    // Gli spazi vengono rimossi durante la digitazione; gli altri caratteri
    // restano visibili per poter segnalare l'errore all'utente.
    this.number.set((rawNumber ?? '').replace(/\s+/g, ''));
  }

  onNumberKeydown(event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
    }
  }

  onBlur(event: FocusEvent): void {
    // Riallinea il valore mostrato nell'input al numero normalizzato (senza
    // spazi) nel caso il DOM ne mostri ancora, es. da incolla.
    const input = event.target as HTMLInputElement | null;
    const normalizedNumber = this.number();

    if (input && input.value !== normalizedNumber) {
      input.value = normalizedNumber;
    }

    this.touched.set(true);
    this.blurred.emit();
  }
}
